import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';

// Helper to render message with links
const renderMessageWithLinks = (message) => {
  // Check if message contains /contact link
  if (message.includes('/contact')) {
    const parts = message.split('/contact');
    return (
      <>
        {parts[0]}
        <Link to="/contact-us" className="underline text-[#ff6b35] hover:text-[#ffc107]">Contact Us</Link>
        {parts[1]}
      </>
    );
  }
  return message;
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [visitorName, setVisitorName] = useState('');
  const [showNameInput, setShowNameInput] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Check for saved session
    const savedSession = localStorage.getItem('chatSession');
    const savedName = localStorage.getItem('chatVisitorName');
    if (savedSession && savedName) {
      // Validate the session still exists
      validateSession(savedSession, savedName);
    }
  }, []);

  const validateSession = async (sid, name) => {
    try {
      const response = await axios.get(`${API}/chat/sessions/${sid}/messages`);
      // Session is valid
      setSessionId(sid);
      setVisitorName(name);
      setShowNameInput(false);
      setMessages(response.data);
    } catch (error) {
      // Session expired or invalid, clear storage
      localStorage.removeItem('chatSession');
      localStorage.removeItem('chatVisitorName');
      setShowNameInput(true);
    }
  };

  useEffect(() => {
    if (sessionId && isOpen) {
      // Connect to WebSocket
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      wsRef.current = new WebSocket(`${wsUrl}/ws/chat/${sessionId}`);
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'admin_message') {
          setMessages(prev => [...prev, data.message]);
        }
      };

      wsRef.current.onerror = () => {
        console.log('WebSocket error - chat will work without real-time updates');
      };

      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [sessionId, isOpen]);

  const loadMessages = async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/sessions/${sid}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const startChat = async () => {
    if (!visitorName.trim()) return;
    
    try {
      const response = await axios.post(`${API}/chat/sessions?visitor_name=${encodeURIComponent(visitorName)}`);
      const sid = response.data.session_id;
      setSessionId(sid);
      localStorage.setItem('chatSession', sid);
      localStorage.setItem('chatVisitorName', visitorName);
      setShowNameInput(false);
      
      // Send initial greeting
      const greeting = await axios.post(`${API}/chat/message`, null, {
        params: {
          session_id: sid,
          message: 'hello',
          sender: 'visitor',
          visitor_name: visitorName
        }
      });
      
      if (greeting.data.bot_response) {
        setMessages([greeting.data.visitor_message, greeting.data.bot_response]);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !sessionId) return;
    
    const messageText = inputValue;
    setInputValue('');
    setIsTyping(true);
    
    try {
      const response = await axios.post(`${API}/chat/message`, null, {
        params: {
          session_id: sessionId,
          message: messageText,
          sender: 'visitor',
          visitor_name: visitorName
        }
      });
      
      if (response.data.bot_response) {
        setMessages(prev => [...prev, response.data.visitor_message, response.data.bot_response]);
      } else if (response.data.visitor_message) {
        setMessages(prev => [...prev, response.data.visitor_message]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (showNameInput) {
        startChat();
      } else {
        sendMessage();
      }
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-[9999] bg-[#ff6b35] hover:bg-[#e55a2b] text-white p-4 rounded-full shadow-lg transition-all duration-300 ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="chat-widget-btn"
      >
        <MessageCircle size={24} />
        <span className="absolute -top-1 -right-1 bg-[#22c55e] w-3 h-3 rounded-full animate-pulse"></span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-6 z-[9999] w-[360px] h-[500px] bg-[#0a1628] rounded-2xl shadow-2xl border border-[#1a2d47] flex flex-col overflow-hidden animate-fade-in" data-testid="chat-window">
          {/* Header */}
          <div className="bg-[#1a4d8c] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full">
                <MessageCircle size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">We Have Your Car</h3>
                <p className="text-white/70 text-xs">Online | Typically replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="close-chat-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {showNameInput ? (
              <div className="text-center py-8">
                <div className="bg-[#1a2d47] p-6 rounded-xl">
                  <User size={48} className="text-[#ff6b35] mx-auto mb-4" />
                  <h4 className="text-white font-semibold mb-2">Welcome!</h4>
                  <p className="text-[#a8b8d0] text-sm mb-4">Please enter your name to start chatting</p>
                  <input
                    type="text"
                    value={visitorName}
                    onChange={(e) => setVisitorName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Your name"
                    className="w-full bg-[#0a1628] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white placeholder-[#a8b8d0] mb-3"
                    data-testid="visitor-name-input"
                  />
                  <button
                    onClick={startChat}
                    className="btn-accent w-full"
                    data-testid="start-chat-btn"
                  >
                    Start Chat
                  </button>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`chat-bubble px-4 py-2 ${
                        msg.sender === 'visitor'
                          ? 'chat-bubble-visitor'
                          : msg.sender === 'admin'
                          ? 'chat-bubble-admin'
                          : 'chat-bubble-bot'
                      }`}
                    >
                      {msg.sender !== 'visitor' && (
                        <p className="text-xs opacity-70 mb-1">{msg.visitor_name || 'Support'}</p>
                      )}
                      <p className="text-sm">{renderMessageWithLinks(msg.message)}</p>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="chat-bubble chat-bubble-bot px-4 py-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          {!showNameInput && (
            <div className="p-4 border-t border-[#1a2d47]">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white placeholder-[#a8b8d0]"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  className="bg-[#ff6b35] hover:bg-[#e55a2b] text-white p-2 rounded-lg transition-colors"
                  data-testid="send-message-btn"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
